import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    useTheme,
} from '@mui/material';
import { ConfigurationStatus } from '@prisma/client';
import { Form, Formik } from 'formik';

import { formatEnumLabel } from 'src/utils/utils';

import { CustomInput } from '../CustomInput';
import { showErrorToast } from '../Toasts/toast';

export type ResourceSectionType = {
    sectionName: string;
    status: ConfigurationStatus;
    pinned: boolean;
    id?: number;
};
interface ResourceSectionFormProps {
    initialValues: ResourceSectionType;
    validationSchema: unknown;
    onSubmit: (values: ResourceSectionType) => void;
    onCancel: () => void;
    isViewModal: boolean;
    setModalTitle: (title: string) => void;
    setIsEdit: (isEdit: boolean) => void;
}

const ResourceSectionForm = ({
    initialValues,
    validationSchema,
    onSubmit,
    onCancel,
    isViewModal,
    setModalTitle,
    setIsEdit,
}: ResourceSectionFormProps) => {
    const theme = useTheme();
    const [isView, setIsView] = useState(isViewModal);

    const handleTriggerEdit = () => {
        setIsView(false);
        setModalTitle('Edit Section');
        setIsEdit(true);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
        >
            {({ values, errors, setFieldValue, handleSubmit, setTouched }) => {
                return (
                    <Form>
                        <Box display="flex" flexDirection="column" gap={2} padding="16px 8px">
                            <CustomInput
                                name="sectionName"
                                label="Section Name"
                                placeholder="Enter Section Name..."
                                onChange={(event) => {
                                    setFieldValue('sectionName', event.target.value);
                                }}
                                value={values.sectionName}
                                error={errors.sectionName}
                                isReadOnly={isView}
                            />

                            {isView && (
                                <IconButton
                                    size="small"
                                    sx={{
                                        color: 'grey',
                                        '&:hover': { background: 'none', color: 'black' },
                                        position: 'absolute',
                                        right: '32px',
                                        top: '82px',
                                    }}
                                    onClick={handleTriggerEdit}
                                >
                                    <EditIcon />
                                </IconButton>
                            )}

                            <FormControl component="fieldset">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={values.pinned}
                                            onChange={(event) => {
                                                setFieldValue('pinned', event.target.checked);
                                            }}
                                            disabled={isView}
                                        />
                                    }
                                    label="Pin to Top?"
                                    labelPlacement="start"
                                    sx={{ marginRight: 0 }}
                                />
                            </FormControl>

                            <Box display="flex" alignItems="center" gap={2}>
                                <Grid item>
                                    <FormLabel sx={{ color: theme.palette.common.black }}>
                                        Status
                                    </FormLabel>
                                </Grid>
                                <Grid item>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            aria-labelledby="status"
                                            defaultValue={
                                                values?.status ?? ConfigurationStatus?.ACTIVE
                                            }
                                            name="status"
                                            row
                                            onChange={(event) => {
                                                setFieldValue('status', event.target.value);
                                            }}
                                            value={values.status}
                                        >
                                            <FormControlLabel
                                                value={ConfigurationStatus.ACTIVE}
                                                control={<Radio />}
                                                label={formatEnumLabel(ConfigurationStatus.ACTIVE)}
                                                disabled={isView}
                                            />
                                            <FormControlLabel
                                                value={ConfigurationStatus.INACTIVE}
                                                control={<Radio />}
                                                label={formatEnumLabel(
                                                    ConfigurationStatus.INACTIVE
                                                )}
                                                disabled={isView}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </Box>

                            <Box display="flex" gap={2} justifyContent="flex-end">
                                <Button
                                    onClick={onCancel}
                                    variant="main"
                                    className="reset-btn btn-medium"
                                >
                                    Cancel
                                </Button>
                                {!isView && (
                                    <Button
                                        onClick={async (event) => {
                                            event.preventDefault();
                                            if (Object.keys(errors).length > 0) {
                                                setTouched({ sectionName: true, status: true });
                                                showErrorToast('Please fill in required fields');
                                            } else {
                                                handleSubmit();
                                            }
                                        }}
                                        variant="main"
                                    >
                                        Confirm
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ResourceSectionForm;
